import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseChart } from './BaseChart.js';
import { formatCurrency, formatPercentage } from '../../utils/dataFormatters.js';
import { getColorByIndex } from '../../utils/colorPalette.js';
import { generateChartAriaLabel } from '../../utils/accessibility.js';
import { formatPaymentMixLabel } from '../../utils/formatPaymentMixLabel.js';
import type { PaymentMixChartProps } from '../../types/analytics.types.js';

/**
 * Production-grade payment mix chart with proper color coding, labels, and accessibility
 */
export const PaymentMixChart = memo<PaymentMixChartProps>(
  ({
    data,
    chartType = 'bar',
    isLoading,
    error,
    emptyMessage,
    emptyHint,
    title,
    subtitle,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  }) => {
    const { t } = useTranslation('admin');
    const resolvedTitle = title ?? t('analytics.charts.paymentMixTitle');
    const resolvedSubtitle = subtitle ?? t('analytics.charts.paymentMixSubtitle');

    const totalRevenue = useMemo(() => {
      return data.reduce((sum, item) => sum + item.revenue, 0);
    }, [data]);

    const sortedData = useMemo(() => {
      return [...data].sort((a, b) => b.revenue - a.revenue);
    }, [data]);

    const chartAriaLabel = useMemo(
      () => ariaLabel || generateChartAriaLabel(resolvedTitle, sortedData.length, resolvedSubtitle),
      [ariaLabel, resolvedTitle, sortedData.length, resolvedSubtitle]
    );

    const formatPaymentLabel = (
      provider: string | null | undefined,
      method: string | null | undefined,
    ): string => formatPaymentMixLabel(t, provider, method);

    // Compute maxRevenue before any early return so hooks are not called conditionally
    const maxRevenue = useMemo(() => {
      return Math.max(...sortedData.map((d) => d.revenue), 0);
    }, [sortedData]);

    if (chartType === 'pie' || chartType === 'donut') {
      return (
        <BaseChart
          isLoading={isLoading}
          error={error}
          emptyMessage={emptyMessage}
          emptyHint={emptyHint}
          title={resolvedTitle}
          subtitle={resolvedSubtitle}
          aria-label={chartAriaLabel}
          aria-describedby={ariaDescribedBy}
        >
          {sortedData.length > 0 ? (
            <div className="flex flex-col gap-5" role="img" aria-label={chartAriaLabel}>
              <div className="flex justify-center items-center min-h-[200px] relative max-md:min-h-[150px] max-[480px]:min-h-[120px]">
                {sortedData.map((item, index) => {
                  const percentage = totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0;
                  const color = getColorByIndex(index);
                  return (
                    <div
                      key={`${item.paymentProvider ?? 'unknown'}-${item.paymentMethod ?? 'unknown'}`}
                      className="absolute w-[150px] h-[150px] max-md:w-[120px] max-md:h-[120px] max-[480px]:w-[100px] max-[480px]:h-[100px] rounded-full"
                      style={{
                        background: `conic-gradient(from 0deg, ${color} 0%, ${color} ${percentage}%, transparent ${percentage}%)`,
                        clipPath: 'polygon(50% 50%, 100% 0%, 100% 100%)',
                      }}
                      role="group"
                      aria-label={t('analytics.charts.paymentItemAria', {
                        provider: formatPaymentLabel(item.paymentProvider, item.paymentMethod),
                        method: '',
                        revenue: formatCurrency(item.revenue),
                        percentage: formatPercentage(percentage),
                      })}
                    >
                      <div className="w-full h-full rounded-full" />
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col gap-2">
                {sortedData.map((item, index) => {
                  const percentage = totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0;
                  const color = getColorByIndex(index);
                  return (
                    <div
                      key={`legend-${item.paymentProvider ?? 'unknown'}-${item.paymentMethod ?? 'unknown'}`}
                      className="flex items-center gap-2.5 text-[13px] max-md:min-h-11 max-md:p-2"
                      role="listitem"
                    >
                      <span
                        className="w-4 h-4 rounded shrink-0"
                        style={{ background: color }}
                      />
                      <span className="flex-1 font-semibold text-[var(--color-an-text)]">
                        {formatPaymentLabel(item.paymentProvider, item.paymentMethod)}
                      </span>
                      <span className="text-xs text-[var(--color-an-text-muted)] font-medium">
                        {formatCurrency(item.revenue)} ({formatPercentage(percentage)})
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </BaseChart>
      );
    }

    return (
      <BaseChart
        isLoading={isLoading}
        error={error}
        emptyMessage={emptyMessage}
        emptyHint={emptyHint}
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        aria-label={chartAriaLabel}
        aria-describedby={ariaDescribedBy}
      >
        {sortedData.length > 0 ? (
          <div className="flex flex-col gap-5" role="img" aria-label={chartAriaLabel}>
            <div className="flex flex-col gap-3">
              {sortedData.map((item, index) => {
                const percentage = totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0;
                const color = getColorByIndex(index);
                return (
                  <div
                    key={`${item.paymentProvider ?? 'unknown'}-${item.paymentMethod ?? 'unknown'}`}
                    className="flex flex-col gap-1.5 cursor-pointer transition-transform p-2 rounded-lg hover:bg-[var(--color-an-surface-hover)] hover:translate-x-1 focus:outline focus:outline-2 focus:outline-[var(--color-an-primary)] max-md:min-h-11 max-md:p-3"
                    role="group"
                    aria-label={t('analytics.charts.paymentItemAria', {
                      provider: formatPaymentLabel(item.paymentProvider, item.paymentMethod),
                      method: '',
                      revenue: formatCurrency(item.revenue),
                      percentage: formatPercentage(percentage),
                    })}
                  >
                    <div className="text-[13px] font-semibold text-[var(--color-an-text)]">
                      {formatPaymentLabel(item.paymentProvider, item.paymentMethod)}
                    </div>
                    <div className="w-full h-8 bg-[var(--color-an-surface-muted,var(--color-an-bg))] rounded-md overflow-hidden relative">
                      <div
                        className="h-full rounded-md flex items-center justify-end px-3 transition-[width] min-w-[120px] max-md:min-w-[100px]"
                        style={{
                          width: `${maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0}%`,
                          background: color,
                        }}
                        aria-label={`${formatCurrency(item.revenue)} (${formatPercentage(percentage)})`}
                      >
                        <span className="text-xs font-bold text-white whitespace-nowrap max-md:text-[11px]">
                          {formatCurrency(item.revenue)} ({formatPercentage(percentage)})
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </BaseChart>
    );
  }
);

PaymentMixChart.displayName = 'PaymentMixChart';

